import { PrfLineItem } from "./prf-line-item.model"
import { v4 as uuid } from 'uuid'
import { Location } from "./location.model"
import { PrfPhoto } from "./prf-photo.model"
import { HIDDEN, PAPER_STATUS, prfStatus } from "src/app/domain/status"
import type { AssetRow } from "src/app/pages/add-prf/prf.vm"

// rt2.prf_statuses ids. InProgress is the app's Draft.
export enum PrfStatusId {
  Submitted = 0,
  Rejected = 1,
  POSubmittedParts = 2,
  POSubmittedServices = 3,
  Completed = 4,
  Removed = 5,
  InProgress = 6,
  Approved = 7,
  RejectedDoNotResubmit = 8,
}

// rt2.prf_types. A Request asks parts for parts (always shop pickup); a Usage
// tells parts what was sold off the technician's truck, billed to the work order.
export enum PrfTypeId {
  Request = 0,
  Usage = 1,
}
export const PRF_TYPE_NAMES: { [id: number]: 'Request' | 'Usage' } = { 0: 'Request', 1: 'Usage' }
export type PrfUrgency = 'Planned' | 'Urgent'

export const PRF_STATUS_NAMES: { [id: number]: string } = {
  0: 'Submitted',
  1: 'Rejected',
  2: 'PO Submitted Parts',
  3: 'PO Submitted Services',
  4: 'Completed',
  5: 'Removed',
  6: 'InProgress',
  7: 'Approved',
  8: 'Rejected Do Not Resubmit',
}

export class Prf {
  uuid: string
  id: number
  is_pending_save: boolean
  is_pending_delete: boolean
  prf_status_id: number
  /** rt2.prf_types: 0 Request, 1 Usage. Undefined until the technician picks one. */
  prf_type_id?: number | null
  // Request: the PRF's asset field edits (see prf.vm.ts assetGroups), the unit's asset rows as the server
  // last sent them, and when the parts are needed.
  asset?: Record<string, string> | null
  asset_rows?: AssetRow[] | null
  need_by_date?: string | null
  urgency?: PrfUrgency | null
  // Usage: the truck the parts were sold from, copied by the server at submit.
  truck_id?: number | null
  truck?: string | null
  prf_notes: string
  work_order_id: number
  work_order_number: string
  prf_line_items: PrfLineItem[]
  status: string
  created_at: string
  updated_at?: string
  company_name: string
  requestor: string
  requestor_id?: number
  last_modified_by: string
  location_name: string
  unit_number: string
  job_type: string
  rejected_reason?: string | null
  /** 1 = made as an MRF before the 2026-11-01 rename, 2 = made as a PRF. The server sets it. */
  version?: number | null
  /** Parts' field, entered in the portal review: the app shows and sends nothing for it. */
  vendor_po_number?: string | null
  prf_file_id?: number | null
  prf_file_created_at?: string | null
  /** The status and type names as the API sends them ('Submitted', 'Request'); the app reads the ids. */
  prf_status?: string | null
  prf_type?: 'Request' | 'Usage' | null

  // App only. pending_photo_deletes holds the uuids of uploaded photos removed
  // here and not yet deleted on the server. local_rev changes on every local
  // save, so a sync can tell whether the PRF was edited while it ran.
  pending_photo_deletes: string[]
  local_rev?: number

  constructor() {
  }

  public async setViewModelFields()
  {
    if (!this.id) {
      this.id = 0
    }
    if (!this.uuid) {
      this.uuid = uuid();
    }
    if (!this.status) {
      this.status = ''
    }
    if (this.is_pending_save === undefined) {
      this.is_pending_save = false
    }
    if (this.is_pending_delete === undefined) {
      this.is_pending_delete = false
    }
    if (!Array.isArray(this.pending_photo_deletes)) {
      this.pending_photo_deletes = []
    }
    this.prf_line_items = (Array.isArray(this.prf_line_items) ? this.prf_line_items : []).map((l: any) => ({
      ...l,
      photos: PrfPhoto.buildMany(l?.photos),
    }))
  }

  static buildOne(obj: any): Prf {
    let target: Prf = Object.assign(new Prf(), obj)
    target.setViewModelFields()
    return target
  }

  static buildMany(objs: any[]): Prf[] {
    const lobjs = []
    for (const i in objs) {
      if (objs.hasOwnProperty(i)) {
        lobjs.push(Prf.buildOne(objs[i]))
      }
    }
    return lobjs
  }

  static isEditable(prf: Prf | null | undefined): boolean {
    if (!prf) return true
    if (prf.is_pending_delete) return false
    const status = prfStatus(prf)
    return status !== HIDDEN && !PAPER_STATUS[status].readOnly
  }

  static isDeletable(prf: Prf | null | undefined): boolean {
    if (!prf || prf.is_pending_delete) return false
    const status = prfStatus(prf)
    return status !== HIDDEN && PAPER_STATUS[status].deletable
  }

  static statusName(prf: Prf): string {
    return PRF_STATUS_NAMES[prf.prf_status_id] ?? prf.status ?? ''
  }

  /** Request or Usage; an PRF from before the types (or a draft that hasn't picked) reads as Request. */
  static typeOf(prf: Pick<Prf, 'prf_type_id'>): PrfTypeId {
    return Number(prf.prf_type_id) === PrfTypeId.Usage ? PrfTypeId.Usage : PrfTypeId.Request
  }

  static isUsage(prf: Pick<Prf, 'prf_type_id'>): boolean {
    return Prf.typeOf(prf) === PrfTypeId.Usage
  }

  // The body for POST prf/ and PUT prf/:id: no photos (they go one at a time
  // to prf/photos) and nothing that only the app keeps.
  static toApiPayload(prf: Prf): any {
    const lines = (prf.prf_line_items || []).map((l: PrfLineItem) => {
      const line: any = {
        id: l.id > 0 ? l.id : 0,
        uuid: l.uuid,
        source: l.source ?? '',
        part_number: l.part_number ?? '',
        description: l.description ?? '',
        quantity: Number.parseInt(String(l.quantity ?? ''), 10) || 0,
      }
      // The API keeps its own value only when these are left out entirely.
      if (l.manufacturer != null) line.manufacturer = l.manufacturer
      if (l.price != null) line.price = l.price
      if (l.unit_of_measure_id != null) line.unit_of_measure_id = l.unit_of_measure_id
      return line
    })
    const usage = Prf.isUsage(prf)
    const payload: any = {
      id: prf.id > 0 ? prf.id : 0,
      uuid: prf.uuid,
      work_order_id: prf.work_order_id,
      prf_type_id: Prf.typeOf(prf),
      asset: usage ? null : (prf.asset && Object.keys(prf.asset).length ? prf.asset : null),
      need_by_date: usage ? null : (prf.need_by_date || null),
      urgency: usage ? null : (prf.urgency ?? 'Planned'),
      prf_notes: prf.prf_notes ?? '',
      prf_status_id: prf.prf_status_id,
      status: prf.status,
      created_at: prf.created_at,
      prf_line_items: lines,
    }
    return payload
  }
}
